export const getTeamOpponentsMaxRound = (teamOpponents) => {
  if (!teamOpponents || Object.keys(teamOpponents).length === 0) {
    return 0;
  }

  return Object.values(teamOpponents).reduce((acc, team) => {
    const rounds = Object.keys(team?.opponentsByRound || {}).map(Number);
    return Math.max(acc, ...rounds, 0);
  }, 0);
};

const getOpponentCountUntilRound = (teamData, opponentId, untilRoundInclusive) => {
  let count = 0;

  Object.entries(teamData?.opponentsByRound || {}).forEach(([roundKey, opponents]) => {
    const roundNumber = Number(roundKey);
    if (roundNumber > untilRoundInclusive) {
      return;
    }

    (opponents || []).forEach((opponent) => {
      const id = typeof opponent === 'string' ? opponent : opponent?.teamId;
      if (id === opponentId) {
        count += 1;
      }
    });
  });

  return count;
};

const hasHistoricalConflict = (teamA, teamB, maxRounds) => {
  if (!teamA || !teamB || maxRounds <= 1) {
    return false;
  }

  return getOpponentCountUntilRound(teamA, teamB.id, maxRounds - 1) > 0;
};

export const buildTeamOpponentAutoAdjustments = (teamOpponents, maxRoundInput) => {
  const maxRounds =
    Number(maxRoundInput) > 0 ? Number(maxRoundInput) : getTeamOpponentsMaxRound(teamOpponents);

  if (!teamOpponents || Object.keys(teamOpponents).length === 0 || maxRounds <= 1) {
    return [];
  }

  const teamsById = Object.entries(teamOpponents).reduce((acc, [teamId, teamData]) => {
    acc[teamId] = { id: teamId, ...teamData };
    return acc;
  }, {});

  const currentRoundTeams = Object.values(teamsById).sort((a, b) => {
    const rankDiff = Number(a.rank || 0) - Number(b.rank || 0);
    if (rankDiff !== 0) {
      return rankDiff;
    }

    // Keep pair ordering deterministic so "first" and "second" are stable.
    const nameDiff = String(a.name || '').localeCompare(String(b.name || ''));
    if (nameDiff !== 0) {
      return nameDiff;
    }

    return String(a.id || '').localeCompare(String(b.id || ''));
  });

  if (currentRoundTeams.length < 4) {
    return [];
  }

  const working = [...currentRoundTeams];
  const suggestions = [];

  const getPairPartner = (list, index) => {
    if (index < 0 || index >= list.length) {
      return null;
    }

    const partnerIndex = index % 2 === 0 ? index + 1 : index - 1;
    if (partnerIndex < 0 || partnerIndex >= list.length) {
      return null;
    }

    return { team: list[partnerIndex], index: partnerIndex };
  };

  const getCurrentRoundOpponentId = (team) => {
    const opponents = team?.opponentsByRound?.[maxRounds] || [];
    if (opponents.length !== 1) {
      return '';
    }

    const opponent = opponents[0];
    return typeof opponent === 'string' ? opponent : opponent?.teamId || '';
  };

  const isMutualCurrentRoundPair = (teamA, teamB) => {
    if (!teamA || !teamB) {
      return false;
    }

    const opponentA = getCurrentRoundOpponentId(teamA);
    const opponentB = getCurrentRoundOpponentId(teamB);
    return opponentA === teamB.id && opponentB === teamA.id;
  };

  for (let pairStart = 0; pairStart < working.length - 1; pairStart += 2) {
    const fixedTeam = working[pairStart];
    const swapTeam = working[pairStart + 1];

    if (!fixedTeam || !swapTeam) {
      continue;
    }

    // Only process real current-round pairs to avoid phantom suggestions.
    if (!isMutualCurrentRoundPair(fixedTeam, swapTeam)) {
      continue;
    }

    if (!hasHistoricalConflict(fixedTeam, swapTeam, maxRounds)) {
      continue;
    }

    const downCandidateIndices = [];
    for (let i = pairStart + 2; i < working.length; i += 1) {
      downCandidateIndices.push(i);
    }

    const upCandidateIndices = [];
    for (let i = pairStart - 1; i >= 0; i -= 1) {
      upCandidateIndices.push(i);
    }

    const swapTeamIndex = pairStart + 1;

    const evaluateCandidate = (candidateIndex) => {
      if (candidateIndex === pairStart || candidateIndex === swapTeamIndex) {
        return null;
      }

      const candidateTeam = working[candidateIndex];
      if (!candidateTeam) {
        return null;
      }

      const candidatePartnerMetaInCurrent = getPairPartner(working, candidateIndex);
      if (!candidatePartnerMetaInCurrent?.team) {
        return null;
      }

      if (!isMutualCurrentRoundPair(candidateTeam, candidatePartnerMetaInCurrent.team)) {
        return null;
      }

      const tentative = [...working];
      // Requirement: keep the first team fixed and only swap the second team.
      [tentative[swapTeamIndex], tentative[candidateIndex]] = [
        tentative[candidateIndex],
        tentative[swapTeamIndex]
      ];

      const replacementTeam = tentative[swapTeamIndex];
      const movedSwapTeamIndex = candidateIndex;
      const movedSwapTeamPartnerMeta = getPairPartner(tentative, movedSwapTeamIndex);
      const movedSwapTeamPartner = movedSwapTeamPartnerMeta?.team || null;

      if (!replacementTeam || !movedSwapTeamPartner) {
        return null;
      }

      if (replacementTeam.id === fixedTeam.id || movedSwapTeamPartner.id === swapTeam.id) {
        return null;
      }

      const fixedTeamConflict = hasHistoricalConflict(fixedTeam, replacementTeam, maxRounds);
      const swapTeamConflict = hasHistoricalConflict(swapTeam, movedSwapTeamPartner, maxRounds);
      const repeatCount = (fixedTeamConflict ? 1 : 0) + (swapTeamConflict ? 1 : 0);

      return {
        candidateIndex,
        replacementTeam,
        movedSwapTeamPartner,
        fixedTeamConflict,
        swapTeamConflict,
        repeatCount
      };
    };

    const pickCandidateInDirection = (candidateIndices) => {
      let bestFallback = null;

      for (const candidateIndex of candidateIndices) {
        const candidate = evaluateCandidate(candidateIndex);
        if (!candidate) {
          continue;
        }

        // Primary rule: first team in the row must get the next team it hasn't played.
        // This is directional and order-sensitive, so return the first match immediately.
        if (!candidate.fixedTeamConflict) {
          return candidate;
        }

        if (
          !bestFallback ||
          candidate.repeatCount < bestFallback.repeatCount ||
          (candidate.repeatCount === bestFallback.repeatCount &&
            bestFallback.fixedTeamConflict &&
            !candidate.fixedTeamConflict)
        ) {
          bestFallback = candidate;
        }
      }

      return bestFallback;
    };

    // Must prefer going down the log first; only try up-log when no down option exists.
    const selected = pickCandidateInDirection(downCandidateIndices) || pickCandidateInDirection(upCandidateIndices);
    if (!selected) {
      continue;
    }

    [working[swapTeamIndex], working[selected.candidateIndex]] = [
      working[selected.candidateIndex],
      working[swapTeamIndex]
    ];

    suggestions.push({
      rowPair: `${Number(fixedTeam.rank || pairStart + 1)}-${Number(swapTeam.rank || pairStart + 2)}`,
      fromTeamId: swapTeam.id,
      fromTeamName: swapTeam.name,
      toTeamId: selected.replacementTeam.id,
      toTeamName: selected.replacementTeam.name,
      affectedTeamName: selected.movedSwapTeamPartner?.name || 'Unknown team',
      direction: selected.candidateIndex > swapTeamIndex ? 'down' : 'up',
      unavoidableRepeat: selected.repeatCount > 0,
      repeatCount: selected.repeatCount,
      fixedTeamId: fixedTeam.id,
      fixedTeamName: fixedTeam.name
    });
  }

  return suggestions;
};
